/**
 * How loud Poisson rain plays: one level for the weather (the rain's hiss,
 * thunder and birdsong) and one for the drops' notes. A student's levels
 * are remembered in this browser only, so the next shower starts where
 * they left it.
 */
import {z} from 'zod';

/** The two volume levels, each a slider position in [0, 1]. */
export interface RainVolume {
  /** The weather: the rain's hiss, thunder and birdsong. */
  rain: number;
  /** The hang drum note each drop plays. */
  drops: number;
}

/**
 * The levels a first shower starts at: the weather at half its tuned
 * loudness, under the notes, which play at about their tuned loudness.
 */
export const DEFAULT_RAIN_VOLUME: RainVolume = {rain: 0.5, drops: 0.7};

/** Where this browser keeps the levels. */
export const RAIN_VOLUME_KEY = 'pai-poisson-rain-volume';

const RainVolumeSchema = z.object({
  rain: z.number().min(0).max(1),
  drops: z.number().min(0).max(1),
});

/**
 * Turns a slider position into a gain, where 1 is the loudness the sounds
 * were tuned at. Loudness is heard on a log scale, so the gain grows with
 * the square of the position; the top of the slider is twice the tuned
 * loudness.
 *
 * @param level - A slider position in [0, 1].
 * @return The gain, in [0, 2].
 * @throws Never.
 */
export function volumeGain(level: number): number {
  const clamped = Math.min(1, Math.max(0, level));
  return 2 * clamped * clamped;
}

/**
 * Reads the levels from storage.
 *
 * @param params.storage - Where the levels live. Omitted means this
 *     browser's `localStorage`.
 * @return The stored levels; {@link DEFAULT_RAIN_VOLUME} when there are
 *     none, they do not parse, or storage cannot be read (private windows
 *     can throw).
 * @throws Never.
 */
export function readRainVolume({
  storage,
}: {storage?: Storage} = {}): RainVolume {
  try {
    const raw = (storage ?? window.localStorage).getItem(RAIN_VOLUME_KEY);
    if (raw === null) {
      return DEFAULT_RAIN_VOLUME;
    }
    const parsed = RainVolumeSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : DEFAULT_RAIN_VOLUME;
  } catch (error) {
    console.debug('[rainVolume] could not read the levels', error);
    return DEFAULT_RAIN_VOLUME;
  }
}

/**
 * Saves the levels to storage.
 *
 * @param params.storage - Where the levels live. Omitted means this
 *     browser's `localStorage`.
 * @param params.volume - The levels to save.
 * @return Nothing; a storage failure is logged and the levels last only
 *     for this shower.
 * @throws Never.
 */
export function writeRainVolume({
  storage,
  volume,
}: {
  storage?: Storage;
  volume: RainVolume;
}): void {
  try {
    (storage ?? window.localStorage).setItem(
      RAIN_VOLUME_KEY,
      JSON.stringify(volume),
    );
  } catch (error) {
    console.debug('[rainVolume] could not save the levels', error);
  }
}
